import UdeskLocales from 'UdeskLocales';
import React, { FC, memo, useMemo } from 'react';
import { Button, Dropdown, InputNumber, Menu, Space } from 'udesk-ui';
import { ArrowDownSOutlined } from '@udesk/icons';
import { hasFeature } from 'src/util/permit';

export const DetectModeMap = {
    Single: 0,
    Multi: 1,
    Stretch: 2,
};

interface TemplateProps {
    value?: {
        detectMode?: number;
        crossingQuantity?: number;
        ignoreWordCount?: number;
        sentenceLength?: number;
    };
    onChange?: (k: TemplateProps['value']) => void;
}

const Template: FC<TemplateProps> = memo((props) => {
    const { value, onChange } = props;

    const options = useMemo(
        () => [
            {
                value: DetectModeMap.Single,
                label: /* 单句检测 */ UdeskLocales['current'].components
                    .checkPointConditionListGather.checkPointCondition.components
                    .problemDetectionMode.index.singleSentenceDetection,
            },
            {
                value: DetectModeMap.Multi,
                label: /* 多句检测 */ UdeskLocales['current'].components
                    .checkPointConditionListGather.checkPointCondition.components
                    .problemDetectionMode.index.multisentenceDetection,
                    disabled: !hasFeature('sys:largeAi:qa:sentences'),
            },
            {
                value: DetectModeMap.Stretch,
                label: /* 跨句检测 */ UdeskLocales['current'].components
                    .checkPointConditionListGather.checkPointCondition.components
                    .problemDetectionMode.index.crosssentenceDetection,
            },
        ],
        []
    );

    const label = useMemo(
        () => options.find((o) => o.value === value?.detectMode)?.label ?? '-',
        [options, value]
    );

    const onModeChange = (key: number) => {
        onChange?.({
            ...value,
            detectMode: key,
        });
    };

    const onMaxStrechSentenceChange = (n: number) => {
        onChange?.({
            ...value,
            crossingQuantity: n,
        });
    };

    const onIgnoreWordsChange = (n: number) => {
        onChange?.({
            ...value,
            ignoreWordCount: n,
        });
    };

    const onMaxStrechWordsChange = (n: number) => {
        onChange?.({
            ...value,
            sentenceLength: n,
        });
    };

    return (
        <Space size="small">
            <div>
                {/* 问题检测方式： */}
                {
                    UdeskLocales['current'].components.checkPointConditionListGather
                        .checkPointCondition.components.problemDetectionMode.index
                        .problemDetectionMethod
                }
            </div>

            <Dropdown
                overlay={
                    <Menu onClick={(e) => onModeChange(+e.key)}>
                        {options.map((o) => (
                            <Menu.Item key={o.value} disabled={o.disabled}>
                                {o.label}
                            </Menu.Item>
                        ))}
                    </Menu>
                }
            >
                <Button type="link" style={{ padding: 0 }}>
                    {label}
                    <ArrowDownSOutlined />
                </Button>
            </Dropdown>

            {value?.detectMode === DetectModeMap.Stretch ? (
                <Space size="small">
                    <Space size="small">
                        <span>
                            {/* 最多跨越 */}
                            {
                                UdeskLocales['current'].components.checkPointConditionListGather
                                    .checkPointCondition.components.problemDetectionMode.index
                                    .mostAcross
                            }
                        </span>
                        <InputNumber<number>
                            min={0}
                            precision={0}
                            value={value?.crossingQuantity}
                            onChange={(e) => onMaxStrechSentenceChange(e)}
                        />
                        <span>
                            {/* 句 */}
                            {
                                UdeskLocales['current'].components.checkPointConditionListGather
                                    .checkPointCondition.components.problemDetectionMode.index
                                    .sentence
                            }
                        </span>
                    </Space>

                    <Space size="small">
                        <span>
                            {/* 忽略不大于 */}
                            {
                                UdeskLocales['current'].components.checkPointConditionListGather
                                    .checkPointCondition.components.problemDetectionMode.index
                                    .ignoreNotGreater
                            }
                        </span>
                        <InputNumber<number>
                            min={0}
                            precision={0}
                            value={value?.ignoreWordCount}
                            onChange={(e) => onIgnoreWordsChange(e)}
                        />
                        <span>
                            {/* 字的句子 */}
                            {
                                UdeskLocales['current'].components.checkPointConditionListGather
                                    .checkPointCondition.components.problemDetectionMode.index
                                    .theSentenceOfTheWord
                            }
                        </span>
                    </Space>

                    <Space size="small">
                        <span>
                            {/* 跨句极限不大于 */}
                            {
                                UdeskLocales['current'].components.checkPointConditionListGather
                                    .checkPointCondition.components.problemDetectionMode.index
                                    .theCrosssentenceLimitIsNotGreater
                            }
                        </span>
                        <InputNumber
                            min={0}
                            precision={0}
                            value={value?.sentenceLength}
                            onChange={(e) => onMaxStrechWordsChange(e)}
                        />
                        <span>
                            {/* 字 */}
                            {
                                UdeskLocales['current'].components.checkPointConditionListGather
                                    .checkPointCondition.components.problemDetectionMode.index.word
                            }
                        </span>
                    </Space>
                </Space>
            ) : null}
        </Space>
    );
});

export default Template;
